/** Formatting for the billing screens, in the workspace's currency ("$49" / "US$ 49", "R$ 169"), whatever the language. */
import type { BillingCurrency } from "@bundle/contracts";
import { useMemo } from "react";
import { useBillingManagers } from "../../context/BillingContext.tsx";
import { useLocale, useT } from "../../i18n/I18nProvider.tsx";

export type BillingFormat = {
  /** A price: no cents when whole ("$49", "$5"). */
  price: (cents: number) => string;
  /** An amount due, always with cents ("$588.00"). */
  amount: (cents: number) => string;
  /** "7 Nov 2026" / "7 de nov. de 2026". */
  date: (ts: number) => string;
  /** Decimal gigabytes, one decimal when needed ("21.4 GB", "30 GB"). */
  gb: (bytes: number) => string;
};

export function useBillingFormat(currency: BillingCurrency = "usd"): BillingFormat {
  const locale = useLocale();
  return useMemo(() => {
    const whole = new Intl.NumberFormat(locale, { style: "currency", currency, minimumFractionDigits: 0, maximumFractionDigits: 0 });
    const cents = new Intl.NumberFormat(locale, { style: "currency", currency, minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const date = new Intl.DateTimeFormat(locale, { day: "numeric", month: "short", year: "numeric" });
    const gb = new Intl.NumberFormat(locale, { maximumFractionDigits: 1 });
    return {
      price: (c) => (c % 100 === 0 ? whole.format(c / 100) : cents.format(c / 100)),
      amount: (c) => cents.format(c / 100),
      date: (ts) => date.format(ts),
      gb: (bytes) => `${gb.format(bytes / 1_000_000_000)} GB`,
    };
  }, [locale, currency]);
}

/** "Ana Souza or Bruno Lima" (owners first), or "an owner or admin" when nobody is known. */
export function useManagerNames(): string {
  const t = useT();
  const locale = useLocale();
  const managers = useBillingManagers(2);
  return useMemo(() => {
    if (managers.length === 0) return t("billing.anAdmin");
    return new Intl.ListFormat(locale, { style: "long", type: "disjunction" }).format(managers.map((m) => m.displayName || m.fullName));
  }, [managers, locale, t]);
}
